import { ReturnDesiredAction, ReturnItemStatus } from '@sleed/bizcommerce-api';
import { DEFAULT_HIDDEN_STATUSES, REFUNDING_ACTIONS } from './constants';

/**
 * "What still owes money" as a single filter choice.
 * There is no dispatch counterpart: with basket-based requests the dispatch record lives on the
 * basket lines, which the grid's per-piece `ReplacementDispatchedAt` cannot see — such a queue
 * would never drain.
 */
export const WORK_QUEUE = {
	/** Approved, money owed, nothing recorded as paid yet. */
	ApprovedUnpaid: 'approvedUnpaid'
} as const;

export type WorkQueuePreset = (typeof WORK_QUEUE)[keyof typeof WORK_QUEUE];

export const SETTLEMENT_STATE = {
	Pending: 'pending',
	Done: 'done'
} as const;

export type SettlementState = (typeof SETTLEMENT_STATE)[keyof typeof SETTLEMENT_STATE];

/** The filter-bar values. Every field is optional; an empty object is "the default open queue". */
export interface ReturnsFilters {
	orderUID?: string;
	customer?: string;
	product?: string;
	workQueue?: WorkQueuePreset | null;
	statuses?: ReturnItemStatus[];
	desiredAction?: ReturnDesiredAction | null;
	/** Refund execution — pending vs recorded. */
	refundExecutionState?: SettlementState | null;
	/** yyyy-MM-dd (day granularity, as the panel collects it). */
	submittedFrom?: string | null;
	submittedTo?: string | null;
}

export const EMPTY_FILTERS: ReturnsFilters = {};

/**
 * Every selector below is a public property of `ReturnGridRowToken`. That is not a coincidence: the
 * server's Δ-06 allow-list is generated from exactly those names and **rejects** the whole request
 * when it sees anything else, in `Filter`, `Sort` and `Group` alike. Renaming a column here without
 * renaming it there turns the grid into a 400, which is the intended failure mode.
 */
const SELECTORS = {
	orderUID: 'OrderUID',
	status: 'Status',
	desiredAction: 'DesiredAction',
	submittedAt: 'SubmittedAt',
	refundExecutedAt: 'RefundExecutedAt',
	customer: ['CustomerFirstName', 'CustomerLastName', 'CustomerEmail', 'CustomerPhone', 'CustomerVatNumber'],
	product: ['ProductName', 'ProductCode', 'ProductBarcode']
} as const;

/** A DevExtreme filter expression: either a `[selector, operation, value]` triple or a group of them. */
export type FilterExpression = unknown[];

/** Joins conditions with an operator, and unwraps the single-condition case DevExtreme dislikes. */
function join(conditions: (FilterExpression | null)[], operator: 'and' | 'or'): FilterExpression | null {
	const present = conditions.filter((c): c is FilterExpression => c != null);

	if (present.length === 0) return null;
	if (present.length === 1) return present[0];

	return present.flatMap((condition, index) => (index === 0 ? [condition] : [operator, condition]));
}

const anyOf = (selectors: readonly string[], operation: string, value: unknown) =>
	join(
		selectors.map(selector => [selector, operation, value]),
		'or'
	);

const hasText = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

/** The end of a day-granularity range is expressed as "< the next day" so the whole day is included. */
function nextDay(value: string): string | null {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;

	date.setDate(date.getDate() + 1);
	return date.toISOString().slice(0, 10);
}

function workQueueConditions(preset: WorkQueuePreset): (FilterExpression | null)[] {
	switch (preset) {
		case WORK_QUEUE.ApprovedUnpaid:
			return [
				[SELECTORS.status, '=', ReturnItemStatus.Approved],
				// Both refund and credit owe the customer money back, and both are settled by the same record.
				join(
					REFUNDING_ACTIONS.map(action => [SELECTORS.desiredAction, '=', action]),
					'or'
				),
				[SELECTORS.refundExecutedAt, '=', null]
			];

		// A retired preset must fall back to the normal filters rather than throw on a spread of `undefined`.
		// Unreachable while the filters are in-memory and typed; the guard is what makes persisting them safe.
		default:
			return [];
	}
}

function settlementCondition(selector: string, state: SettlementState | null | undefined): FilterExpression | null {
	if (state === SETTLEMENT_STATE.Pending) return [selector, '=', null];
	if (state === SETTLEMENT_STATE.Done) return [selector, '<>', null];

	return null;
}

/**
 * The status condition. With nothing selected the list still hides "Rejected" and "Completed": the
 * default view is the open queue, not a journal.
 */
function statusCondition(statuses: ReturnItemStatus[] | undefined): FilterExpression | null {
	if (statuses?.length)
		return join(
			statuses.map(status => [SELECTORS.status, '=', status]),
			'or'
		);

	return join(
		DEFAULT_HIDDEN_STATUSES.map(status => [SELECTORS.status, '<>', status]),
		'and'
	);
}

/**
 * Builds the whole `filter` expression from the filter bar.
 *
 * `returns/getall` binds `DataSourceLoadOptions` and nothing else — there is no `additionalFiltering`
 * parameter to lean on, because the Δ-06 allow-list is defined over the grid's own column names and
 * rejects anything else outright. So the bar's conditions are translated here into allow-listed
 * selectors instead.
 */
export function buildReturnsFilter(filters: ReturnsFilters): FilterExpression | null {
	const conditions: (FilterExpression | null)[] = [];

	if (hasText(filters.orderUID)) conditions.push([SELECTORS.orderUID, 'contains', filters.orderUID.trim()]);
	if (hasText(filters.customer)) conditions.push(anyOf(SELECTORS.customer, 'contains', filters.customer.trim()));
	if (hasText(filters.product)) conditions.push(anyOf(SELECTORS.product, 'contains', filters.product.trim()));

	// A work-queue preset pins the status and the action itself, so the other choices stand down
	// rather than fight it. Judged by the conditions it yields and not by mere presence, so a retired preset
	// falls back to the normal filters instead of disabling them — same rule as admin-v1.
	const presetConditions = filters.workQueue ? workQueueConditions(filters.workQueue).filter(Boolean) : [];

	if (presetConditions.length > 0) {
		conditions.push(...presetConditions);
	} else {
		conditions.push(statusCondition(filters.statuses));

		if (filters.desiredAction != null) conditions.push([SELECTORS.desiredAction, '=', filters.desiredAction]);

		conditions.push(settlementCondition(SELECTORS.refundExecutedAt, filters.refundExecutionState));
	}

	if (hasText(filters.submittedFrom)) conditions.push([SELECTORS.submittedAt, '>=', filters.submittedFrom]);

	if (hasText(filters.submittedTo)) {
		const upperBound = nextDay(filters.submittedTo);
		if (upperBound) conditions.push([SELECTORS.submittedAt, '<', upperBound]);
	}

	return join(conditions, 'and');
}

/** How many filter-bar choices are active — drives the "clear filters" affordance. */
export function activeFilterCount(filters: ReturnsFilters): number {
	return [
		hasText(filters.orderUID),
		hasText(filters.customer),
		hasText(filters.product),
		Boolean(filters.workQueue),
		Boolean(filters.statuses?.length),
		filters.desiredAction != null,
		Boolean(filters.refundExecutionState),
		hasText(filters.submittedFrom),
		hasText(filters.submittedTo)
	].filter(Boolean).length;
}
